import { FunctionComponent, useCallback, useEffect } from "react";
import { CheckIcon, CloseIcon } from "@form/components/common/icons";
import { useTypedSelector } from "@/stores";
import { Button, TextInput, Loader } from "@mantine/core";
import Link from "next/link";
import useUserEmail from "@form/hooks/useUserEmail";
import useAccount from "@/hooks/useAccount";
import { validateEmail } from "@form/utils/validate";
import useVerifyCampaign from "@form/hooks/useVerifyCampaign";
import mixpanel from "mixpanel-browser";
import { revokeEmail } from "@/form/services/account";
import { confirm } from "@form/services/modal";

import type { EmailForm } from "@form/components/form/modules/email";
import type { SubmitError } from "@form/hooks/useVerifyCampaign";

const EmailVerify: FunctionComponent<{
  form: EmailForm;
  available: boolean;
  needFormVerify: boolean;
  submitError?: SubmitError;
}> = ({ form, available, needFormVerify, submitError }) => {
  const userInfo = useTypedSelector((state) => {
    return state.account.userInfo;
  });

  const { isLogin, login } = useAccount();

  const needVerify = form.values.verifyEmail && available;

  const { email, setEmail, verify, sent, verifying, onceSend } =
    useUserEmail(needVerify);

  const isEmailValid = validateEmail(email);

  const isEmailVerified = !!userInfo?.is_email_verified;

  const {
    changeVerifyState,
    changeForm,
    form: userForm,
    result,
  } = useVerifyCampaign("email");

  const serverError = submitError?.[form.values.uid]?.verifyEmail;

  const showError = (needFormVerify && !result.email) || serverError;

  useEffect(() => {
    if (!isEmailValid) {
      return changeVerifyState(false);
    }
    if (needVerify && !isEmailVerified) {
      return changeVerifyState(false);
    }
    changeVerifyState(true);
  }, [needVerify, isEmailVerified, isEmailValid]);

  let rightSection = null as any;

  useEffect(() => {
    changeForm(email);
  }, [email]);

  const disconnect = useCallback(async () => {
    if (isEmailVerified) {
      await revokeEmail();
    }
  }, [isEmailVerified]);

  const sendEmail = useCallback(() => {
    if (!isEmailValid) {
      return;
    }
    if (!isLogin) {
      return login();
    }
    mixpanel.track("verify_email");
    verify();
  }, [isLogin, verify, isEmailValid]);

  if (needVerify && !isEmailVerified) {
    if (verifying && !sent) {
      rightSection = (
        <div className="flex justify-end w-full pr-3">
          <Loader size="xs"></Loader>
        </div>
      );
    } else if (sent) {
      rightSection = (
        <div className="text-body font-bold text-gray_95 text-right w-full pr-3">
          Sent
        </div>
      );
    } else if (onceSend) {
      rightSection = (
        <div
          className={`text-body font-bold text-primary action text-right  w-full pr-3 ${
            isEmailValid ? "" : "opacity-50 hover:cursor-not-allowed"
          }`}
          onClick={sendEmail}
        >
          Re-send
        </div>
      );
    } else {
      rightSection = (
        <div
          className={`text-body font-bold text-primary action text-right ${
            isEmailValid ? "" : "opacity-50 hover:cursor-not-allowed"
          }`}
          onClick={sendEmail}
        >
          Send verification
        </div>
      );
    }
  } else {
    rightSection = (
      <div
        className="action translate-x-[38px] ml-1 w-[14px] h-[14px] rounded-full bg-primary flex items-center justify-center"
        onClick={() => {
          confirm({
            title: "Are you sure to disconnect your account?",
            okText: "Disconnect",
            okType: "danger",
            onOk() {
              disconnect();
            },
          });
        }}
      >
        <CloseIcon className="w-[6px]"></CloseIcon>
      </div>
    );
  }

  return (
    <div className={available ? "" : "relative opacity-40 disable"}>
      <div className="flex items-center">
        <div className="flex-1">
          <div className="flex items-center">
            {form.module.icon && (
              <form.module.icon className="dark:fill-[#3C86FB]"></form.module.icon>
            )}
            <span className="text-body text-dark1 font-bold ml-2 dark:text-gray_f9">
              Email
            </span>
          </div>
        </div>
      </div>
      <TextInput
        readOnly={(needVerify && isEmailVerified) || (verifying && !sent)}
        value={available ? email : ""}
        className="mt-2"
        size="md"
        classNames={{
          input: `border-[#e7e7e7] dark:!bg-[#192844] dark:border-[#2F5EAA] dark:text-gray_f9 ${
            showError ? "bg-error/10 border border-error" : ""
          }`,
        }}
        placeholder="Your email"
        rightSectionWidth={130}
        rightSection={rightSection}
        disabled={!!userForm.id}
        onChange={(e) => {
          setEmail(e.currentTarget.value);
        }}
      ></TextInput>
      {needVerify && (
        <div className={`mt-2`}>
          <div
            className={`py-2 px-4 bg-gray_f9 rounded-lg flex items-center justify-between dark:bg-[#192844] ${
              showError ? "bg-error/10 border border-error" : ""
            }`}
          >
            <div className="flex items-center text-body text-dark1 dark:text-gray_f9">
              Verify your email
            </div>
            <CheckIcon
              className={
                isEmailVerified
                  ? "fill-green dark:fill-dark_green"
                  : "dark:fill-[#94949C]"
              }
            ></CheckIcon>
          </div>
          <div
            className={`text-error text-sm mt-1 ${
              showError && !isEmailVerified ? "flex" : "hidden"
            }`}
          >
            This is required
          </div>
        </div>
      )}
      <div
        className={`text-error text-sm mt-1 ${showError ? "flex" : "hidden"}`}
      >
        This is required
      </div>
    </div>
  );
};

export default EmailVerify;
